/**
 * Phase F2 — Buyer NFT Library
 * ----------------------------
 * Surfaces Phase 5 NFT lifecycle states via the buyer UI.
 *
 * Endpoints used (read-only):
 *   GET /api/buyer/nft-library                — counts + sample for all states
 *   GET /api/buyer/nft-library?state=OWNED    — filtered list (PENDING/OWNED/SOLD/TRADED/BURNED)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layers, Clock, Check, ArrowLeftRight, Flame, ShoppingBag, ImageOff,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'OWNED', label: 'Owned', Icon: Check, color: theme.colors.status.success },
  { key: 'PENDING', label: 'Pending', Icon: Clock, color: theme.colors.status.warning },
  { key: 'SOLD', label: 'Sold', Icon: ShoppingBag, color: theme.colors.status.info },
  { key: 'TRADED', label: 'Traded', Icon: ArrowLeftRight, color: theme.colors.accent.teal },
  { key: 'BURNED', label: 'Burned', Icon: Flame, color: theme.colors.status.error },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

const StatePill = ({ state }) => {
  const meta = TABS.find((t) => t.key === state) || { color: theme.colors.text.muted, label: state };
  return (
    <span
      data-testid={`nft-state-pill-${state}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999,
        background: `${meta.color}1A`, color: meta.color,
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {meta.label || state}
    </span>
  );
};

const NFTCard = ({ nft }) => {
  const img = nft.image_url || nft.nft_image_url || nft.image || null;
  const ownerLabel = nft.owner_email || nft.owner_id || '—';
  return (
    <div data-testid={`nft-card-${nft.id}`} style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
      <div style={{
        height: 160, background: theme.colors.bg.tertiary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderBottom: `1px solid ${theme.colors.borders}`,
      }}>
        {img ? (
          <img src={img} alt={nft.name || 'NFT'} style={{
            width: '100%', height: '100%', objectFit: 'cover',
          }} onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <ImageOff size={36} color={theme.colors.text.muted} />
        )}
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: 15 }}>
            {nft.name || nft.nft_name || `NFT ${(nft.id || '').slice(0, 8)}`}
          </div>
          <StatePill state={nft.lifecycle_state || nft.status} />
        </div>
        <div style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 12 }}>
          {nft.portfolio_name || nft.portfolio_id || '—'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div>
            <div style={{ color: theme.colors.text.muted, marginBottom: 2 }}>Assigned</div>
            <div style={{ color: theme.colors.text.primary, fontWeight: 600 }}>
              {fmtDate(nft.acquired_at || nft.assigned_at || nft.minted_at || nft.created_at)}
            </div>
          </div>
          <div>
            <div style={{ color: theme.colors.text.muted, marginBottom: 2 }}>Current owner</div>
            <div
              style={{
                color: theme.colors.text.primary, fontWeight: 600,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              title={ownerLabel}
            >
              {ownerLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BuyerNFTLibrary() {
  const [activeTab, setActiveTab] = useState('OWNED');
  const [library, setLibrary] = useState({ counts: {}, library: {} });
  const [filteredNfts, setFilteredNfts] = useState([]);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/buyer/nft-library`, auth);
        if (cancelled) return;
        const data = res.data || {};
        setLibrary({
          counts: data.counts || {},
          library: data.library || {},
        });
      } catch (e) {
        if (!cancelled) toast.error('Failed to load NFT library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    const fetchState = async () => {
      try {
        const res = await axios.get(`${API}/buyer/nft-library?state=${activeTab}`, auth);
        if (cancelled) return;
        setFilteredNfts(res.data?.nfts || []);
      } catch (e) {
        if (!cancelled) setFilteredNfts(library.library?.[activeTab] || []);
      }
    };
    fetchState();
    return () => { cancelled = true; };
  }, [activeTab, auth, library.library]);

  const nfts = filteredNfts;

  return (
    <div data-testid="buyer-nft-library-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary,
          margin: 0, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Layers size={26} color={theme.colors.primary} />
          NFT Library
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Your NFTs grouped by lifecycle state.
        </p>
      </div>

      {/* Tabs */}
      <div
        data-testid="nft-library-tabs"
        style={{
          display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap',
        }}
      >
        {TABS.map((tab) => {
          const Icon = tab.Icon;
          const active = activeTab === tab.key;
          const count = library.counts?.[tab.key] || 0;
          return (
            <button
              key={tab.key}
              data-testid={`nft-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? tab.color : theme.colors.borders}`,
                background: active ? `${tab.color}15` : theme.colors.bg.card,
                color: active ? tab.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
                transition: 'all 150ms ease',
              }}
            >
              <Icon size={16} />
              {tab.label}
              <span style={{
                marginLeft: 4, background: active ? tab.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div data-testid="nft-library-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading NFT library…
        </div>
      ) : nfts.length === 0 ? (
        <div data-testid="nft-library-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center' }}>
          <ImageOff size={36} color={theme.colors.text.muted} style={{ margin: '0 auto 8px' }} />
          <div style={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
            No {activeTab.toLowerCase()} NFTs.
          </div>
        </div>
      ) : (
        <div
          data-testid="nft-library-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {nfts.map((n) => (
            <NFTCard key={n.id || `${n.portfolio_id}-${n.unit_number}`} nft={n} />
          ))}
        </div>
      )}
    </div>
  );
}
